import { useEffect } from 'react';
import { NavLink, Outlet } from 'react-router';
import { Logo } from '@/components/Logo';
import { Toaster, TooltipProvider } from '@/components/ui';
import { useAuth } from '@/auth/store';
import { useIsMobile } from '@/lib/useMediaQuery';
import { useSyncScheduler } from '@/sync/useSyncScheduler';
import { useCore } from '@/data/core';
import { useTeam } from '@/team/store';
import { useTeamPolling } from '@/team/useTeamPolling';
import { usePlanLoader } from '@/plan/store';
import { useTimerHeartbeat } from '@/timer/useTimer';
import { AccountPill } from './AccountPill';
import { BottomNav } from './BottomNav';
import { OfflineBadge } from './OfflineBadge';
import { PlanBanner } from './PlanBanner';
import { StatusPill } from './StatusPill';
import { TrialBadge } from './TrialBadge';
import { FeedbackButton } from '@/features/feedback/FeedbackButton';
import { FeedbackDialog } from '@/features/feedback/FeedbackDialog';
import { SessionLostNotice } from './SessionLostNotice';

const TABS = [
  { to: '/timer', label: 'Timer' },
  { to: '/sessions', label: 'Sessions' },
  { to: '/projects', label: 'Projects' },
  { to: '/team', label: 'Team', teamOnly: true },
  { to: '/settings', label: 'Settings' },
];

/** Desktop: logo left, underline tabs centre, status right. Phones: the tabs move to a bottom bar (spec §2). */
export function AppShell() {
  const bootstrap = useAuth((s) => s.bootstrap);
  useEffect(() => { void bootstrap(); }, [bootstrap]);
  // Ask the browser not to evict our IndexedDB after inactivity (Safari). Here rather than at boot, because
  // boot also runs on the landing page, where a storage permission prompt is asked of someone who has not yet
  // chosen to use the app. Requesting it once the shell is on screen ties the prompt to storing real sessions.
  useEffect(() => { if (navigator.storage?.persist) void navigator.storage.persist(); }, []);
  useSyncScheduler();
  useTeamPolling();
  usePlanLoader();
  useTimerHeartbeat(); // the clock, the chime and the tab title belong to the shell, not to the Timer screen
  const hasTeam = !!useTeam((s) => s.membership);
  const mobile = useIsMobile();
  const ready = useCore((s) => s.ready);

  // The local store is read before anything is drawn, so no screen paints a default it is about to replace.
  // The page already carries the resolved theme from index.html, so this reads as loading rather than a flash.
  if (!ready) return <div className="min-h-dvh bg-canvas" />;

  return (
    <TooltipProvider>
      <div className="min-h-dvh flex flex-col bg-canvas text-ink">
        {/* Equal side tracks, so the centred nav cannot drift when the status pill changes width. */}
        <header className="h-[60px] shrink-0 grid grid-cols-[1fr_auto_1fr] items-center gap-3 px-4 md:px-7 border-b border-header-rule">
          <NavLink to="/timer" className="flex items-center gap-[10px] no-underline text-ink justify-self-start">
            <Logo />
            <span className="font-extrabold text-[17px]">Timato</span>
          </NavLink>
          {!mobile && <nav className="hidden md:flex gap-[26px] text-row-title" aria-label="Primary">
            {TABS.filter((t) => !t.teamOnly || hasTeam).map((t) => (
              <NavLink key={t.to} to={t.to} className={({ isActive }) =>
                `py-[6px] no-underline border-b-2 transition-colors ${isActive ? 'text-ink border-accent' : 'text-ink-2 border-transparent hover:text-ink'}`}>
                {t.label}
              </NavLink>
            ))}
          </nav>}
          <div className="flex items-center gap-2 md:gap-3 justify-self-end">
            <OfflineBadge />
            <StatusPill />
            <TrialBadge />
            <AccountPill />
          </div>
        </header>
        <PlanBanner />
        {/* A column that fills what the header and the plan banner leave, so a screen can fill it too (flex-1) rather
            than subtract a header height it cannot know: the banner is sometimes there and wraps on a phone (TW-12).
            Direct children stay full width, as they were as blocks, so max-width plus mx-auto still centres them. */}
        <main className="flex-1 min-h-0 flex flex-col *:w-full pb-[76px] md:pb-0">
          <Outlet />
        </main>
        {mobile && <BottomNav hasTeam={hasTeam} />}
        <FeedbackButton />
        <FeedbackDialog />
        <SessionLostNotice />
        <Toaster />
      </div>
    </TooltipProvider>
  );
}
